<template>
  <zm-dialog
    class="opc-zod"
    title=""
    v-model="visible"
    width="30%"
    append-to-body
    show-close
    @closed="handleCancel"
    :class="{
      'opc-modal': $context.is.modal,
    }"
  >
    <div class="opc-zod__head">
      <h2>{{ $t('billing.delta.cur_plan_details') }}</h2>
      <span>{{ plansCount }} {{ itemDesc }}</span>
    </div>
    <PlanDetail ref="planDetail" />
    <div class="opc-zod__footer">
      <current-charge :modifier="currentChargeAmountModifier" :inner-divider="false" />
    </div>
  </zm-dialog>
</template>

<script lang="jsx">
import { mapGetters } from 'vuex';
import Cart from '@/plans/mx/shopping-cart';
import CurrentCharge from '@/views/config/shopping-cart/current.vue';
import { multipleCapacityPlanTypes, planTypes } from '@/common/constants';
import { CART_DISPLAY_MODE } from '@/plans/mx/shopping-cart.vue';
import { isEmptyVal } from '@/utils';
import { dateTimeFormat, dateTimeFormatWithClientZone } from '@/utils/dateformat';
import { calculateDiscountWithDiscountType } from '@/views/config/coupon/helper/utils';
import _ from 'lodash';
import { translation } from '@/plugins/i18n';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { getViewCurrentPlanName } from '@/components/view-current-dialog/helper';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

/**
 * Filter bought plans based on selected plans
 * Handles filtering logic for multiple capacity plans and ZOOM_ONE_BUNDLE
 * @param {Array} boughtPlans - List of bought plans
 * @param {Array} selectedPlans - List of selected plans
 * @returns {Array} Filtered bought plans
 */
const filterBoughtPlansBySelected = (boughtPlans, selectedPlans, multipleSubscriptionScope) => {
  if (!boughtPlans || !selectedPlans) return [];

  // Group selected plans by type
  const planTypeMapInfo = _.groupBy(selectedPlans, 'type');
  const planTypeList = _.keys(planTypeMapInfo);

  return _.filter(boughtPlans, (x) => {
    const planType = x?.type;
    const category = x?.category;

    // Filter by plan type
    if (!_.includes(planTypeList, planType)) return false;
    if (multipleSubscriptionScope.some(scope => scope.type === planType && scope.category === category)) {
      // only show current edit plan.
      return selectedPlans.some(plan => plan.plan_code === x.plan_code);
    }

    // Deal with multiple capacity plan list
    if (_.includes(multipleCapacityPlanTypes, planType)) {
      return x?.capacity === _.get(planTypeMapInfo, [planType, 0, 'capacity']);
    }

    // Biz plus only should show current edit one, distinguish by category
    if (planType === planTypes.ZOOM_ONE_BUNDLE) {
      const categories = _.keys(_.groupBy(selectedPlans, 'category'));
      if (!_.includes(categories, category)) {
        return false;
      }
    }

    return true;
  });
};

const planShouldShowRenewsDate = (planList = []) => {
  if (!planList || planList.length === 0 || planList.length === 1) return false;

  // Group plans by billing_cycle
  const cycleGroups = _.chain(planList)
    .groupBy('billing_cycle')
    .keys()
    .value();

  if (cycleGroups.length > 1) return false;

  const expireTimes = _.chain(planList)
    .map(p => p?.cartInfo?.planExpireTime)
    .filter(Boolean)
    .uniq()
    .value();

  return expireTimes.length > 1;
}

const PlanDetail = {
  mixins: [Cart],
  computed: {
    ...mapGetters(['boughtPlans', 'discountWithAmountMap', 'getSelectedPlans', 'multipleSubscriptionScope']),
    currentSelectedPlanTypeMapInfo() {
      return _.groupBy(this.getSelectedPlans, 'type');
    },
    currentSelectedPlanTypeList() {
      return _.keys(this.currentSelectedPlanTypeMapInfo);
    },
    mimic() {
      const plans = filterBoughtPlansBySelected(this.boughtPlans, this.getSelectedPlans, this.multipleSubscriptionScope);
      const mode = CART_DISPLAY_MODE.BOUGHT;

      return {
        mode,
        plans,
      };
    },
    mode() {
      return this.mimic.mode;
    },
    // Cache shouldShowRenews result to avoid recalculating on every render
    shouldShowRenews() {
      return planShouldShowRenewsDate(this.mimic.plans);
    },
  },
  methods: {
    compute_delta_discount(plan) {
      const basicInfo = { discount: 0, expireDate: '' };
      if (isEmptyVal(plan)) return basicInfo;

      const { discountPlans } = this.discountWithAmountMap;
      const planCode = plan?.plan_code;
      const discountPlanInfo = discountPlans?.[planCode];
      if (isEmptyVal(discountPlanInfo)) return basicInfo;

      const boughtQuantity = plan?.cartInfo?.selected_number;
      const expireDate = discountPlanInfo?.expireDate ?? '';
      const discount = calculateDiscountWithDiscountType({
        discountType: discountPlanInfo?.couponDiscountType,
        discount: discountPlanInfo?.discount,
        amount: plan?.amount * boughtQuantity,
        quantity: boughtQuantity,
      });

      return {
        discount,
        expireDate,
      };
    },
    mx_delta_discount_view(plan) {
      const { expireDate, discount } = this.compute_delta_discount(plan);
      if (!discount) return null;

      const expiresDesc = expireDate ?
        ` (${this.$t('billing.coupon.expiresDesc', [dateTimeFormat(expireDate)])})` : '';
      const cartDiscountLabel = `${this.$t('billing.coupon.discount')} ${expiresDesc}`;

      return (
        <div class="opc-cart-item__discount __PLACEHOLDER_discount_title__">
          <span>{cartDiscountLabel}</span>
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(discount)}</span>
        </div>
      );
    },
    renderTitle(plan) {
      const planName = getViewCurrentPlanName(plan);

      return (
        <div>
          {this.mx_title(
            planName,
            this.$money(this.mx_price$(plan)),
            plan?.plan_code,
            undefined,
          )}
        </div>
      );
    },
    mx_license(num, unit = this.$t('billing.planform.unit.license')) {
      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    // Render renews date for a plan
    mx_renews_date(plan) {
      const expireTime = plan?.cartInfo?.planExpireTime;
      if (!expireTime) return null;
      const formattedDate = dateTimeFormatWithClientZone(expireTime);
      return (
        <div class="opc-cart-item__text first-capitalize">
          {this.$t('billing.order_summary.renews', { date: formattedDate })}
        </div>
      );
    },
    $renderPrice(plans) {
      const planList = plans || this.mx_plans;
      // Use computed property when using mimic.plans, otherwise calculate on the fly
      const shouldShowRenews = this.shouldShowRenews;

      return planList.map((plan) => {
        return (
          <div class="opc-cart-item">
            {this.renderTitle(plan)}
            {this.mx_delta_discount_view(plan)}
            {this.mx_license$(plan)}
            {this.mx_expire_date(this.expiredDate(plan)) || this.mx_cycle(plan.billing_cycle)}
            {shouldShowRenews && this.mx_renews_date(plan)}
          </div>
        );
      });
    },
    expiredDate() {
      return '';
    },
  },
  render() {
    return <div>{this.$renderPrice(this.mimic.plans)}</div>;
  },
};

export default {
  mixins: [ConfirmComponent],
  data() {
    return {
      planType: '',
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'getSelectedPlans', 'getSelectedPlansAmount', 'multipleSubscriptionScope']),
    plansCount() {
      return this.getSelectedPlans?.length || 1;
    },
    trackingName() {
      return getTrackingProductNameByPlanType(this.planType);
    },
    filteredBoughtPlans() {
      return filterBoughtPlansBySelected(this.boughtPlans, this.getSelectedPlans, this.multipleSubscriptionScope);
    },
    // Cache shouldShowRenews result to avoid recalculating on every method call
    shouldShowRenews() {
      return planShouldShowRenewsDate(this.filteredBoughtPlans);
    },
    nextChargeDateVaryDesc() {
      return this.$t('billing.order_summary.next_charge_date_vary');
    },
    itemDesc() {
      return translation('billing.order_summary.item_ss', this.plansCount);
    },
  },
  components: {
    PlanDetail,
    CurrentCharge,
  },
  methods: {
    handleCancel() {
      this.$tracking.buyInteraction('product details', `${this.trackingName} plan details - exit`);
    },
    async currentChargeAmountModifier(list) {
      // HACK CurrentCharge > nextBill.amount > getSelectedPlans, we have to exclude the delta part
      const planListGroupByCycle = _.chain(this.getSelectedPlans)
        .groupBy('billing_cycle')
        .value(); // this is edit delta flow

      // Vue3 Fix: Wait for DOM to be ready
      await this.$nextTick();
      // Use computed property instead of calling function directly
      const shouldShowRenews = this.shouldShowRenews;
      return _.map(list, (x) => {
        if (_.has(planListGroupByCycle, x.billingCycle)) {
          const planList = _.get(planListGroupByCycle, [x.billingCycle]);
          const { boughtPlanAmount, boughtPlanDiscount } = _.reduce(planList, (pre, cartPlan) => {
            const boughtPlan = _.find(this.boughtPlans, (b) => b.plan_code === cartPlan.plan_code);
            pre.boughtPlanAmount += this.$refs.planDetail.mx_price(boughtPlan);
            pre.boughtPlanDiscount += this.$refs.planDetail.compute_delta_discount(boughtPlan)?.discount;
            return pre;
          }, { boughtPlanAmount: 0, boughtPlanDiscount: 0 });
          // boughtPlanAmount is bought plan's total standard amount = 3 plans standard amount 6000
          // boughtPlanDiscount is bought plan's total discount amount = 3 plans discount amount 3000 with 50% discount
          x.amount = boughtPlanAmount - boughtPlanDiscount;
        } else {
          x.amount = 0;
        }

        if (shouldShowRenews) {
          x.renewDate = this.nextChargeDateVaryDesc;
        }
        return x;
      });
    },
  },
  watch: {
    visible: {
      handler(val) {
        if (val) {
          this.$tracking.buyInteraction('product details', `${this.trackingName} current plan details - impression`);
        }
      },
    },
  },
};
</script>

<style lang="scss">
.opc-zod {

  &.opc-modal {
    .zbo-dialog {
      margin: 0 !important;
      width: 85% !important;
      min-width: auto !important;
      height: auto;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &__head {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #dfdfe4;
    padding-bottom: 8px;
    margin-bottom: 12px;

    h2 {
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
      text-align: left;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }

    span {
      font-size: 14px;
      font-weight: 500;
      line-height: 32px;
      text-align: right;
      color: var(--zoom-color-text-stronger-neutral, #2A2B2D);
    }
  }

  .opc-cart-item {
    margin-bottom: 12px;
  }

  &__footer {
    padding-top: 12px;
    border-top: 1px solid #dfdfe4;
  }
}
</style>
